import { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ApiOutlined,
  CheckCircleFilled,
  LinkOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  getCapabilityProvider,
  listCapabilityBindings,
  listCapabilityFamilies,
} from '@/api/capabilities';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { BindingView, FamilyCatalogueEntry } from '@/api/types';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { TableSkeleton } from '@/components/TableSkeleton';
import { EntitlementMatrix } from '@/components/EntitlementMatrix';
import { usePageTitle } from '@/components/pageTitle';
import { BindingDrawer } from '@/integrations/BindingDrawer';
import { TierBadge } from '@/integrations/tierBadge';
import { WebhooksSection } from '@/integrations/WebhooksSection';

const { Text } = Typography;

interface FamilyRow {
  family: FamilyCatalogueEntry;
  binding?: BindingView;
}

export function IntegrationsPage() {
  usePageTitle('Integrations');
  const { message } = App.useApp();

  const [families, setFamilies] = useState<FamilyCatalogueEntry[]>([]);
  const [bindings, setBindings] = useState<BindingView[]>([]);
  const [tiers, setTiers] = useState<Record<string, string | undefined>>({});
  const [loading, setLoading] = useState(true);
  const [familiesDown, setFamiliesDown] = useState(false);
  const [bindingsUnavailable, setBindingsUnavailable] = useState(false);
  const [editFamily, setEditFamily] = useState<FamilyCatalogueEntry | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFamiliesDown(false);
    setBindingsUnavailable(false);
    setTiers({});
    try {
      setFamilies(await listCapabilityFamilies());
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setFamiliesDown(true);
      else message.error(n.message);
      setFamilies([]);
    }
    // Bindings are hub-scoped and may 404/503 independently of the (always-on) family catalogue.
    let boundList: BindingView[] = [];
    try {
      boundList = await listCapabilityBindings();
      setBindings(boundList);
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n) || n.httpStatus === 503) setBindingsUnavailable(true);
      else message.error(n.message);
      setBindings([]);
    } finally {
      setLoading(false);
    }
    // Best-effort connector-tier resolution (F-AD-14): resolve each BOUND provider's descriptor for
    // its tier. There is no list-providers endpoint, so we resolve by id per bound family. Silently
    // degrades — a 403/404 (e.g. get-provider needs platform_admin) leaves the tier "unspecified".
    if (boundList.length > 0) {
      const resolved = await Promise.allSettled(
        boundList.map((b) => getCapabilityProvider(b.family, b.version, b.providerId)),
      );
      const map: Record<string, string | undefined> = {};
      resolved.forEach((r, i) => {
        if (r.status === 'fulfilled') map[boundList[i].providerId] = r.value.tier;
      });
      setTiers(map);
    }
    // message stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo<FamilyRow[]>(() => {
    const byFamily = new Map<string, BindingView>();
    bindings.forEach((b) => byFamily.set(`${b.family}.${b.version}`, b));
    return families.map((f) => ({ family: f, binding: byFamily.get(`${f.family}.${f.version}`) }));
  }, [families, bindings]);

  const columns: ColumnsType<FamilyRow> = [
    {
      title: 'Capability family',
      key: 'family',
      render: (_, r) => (
        <div style={{ minWidth: 220 }}>
          <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="ol-mono">{r.family.family}</span>
            <Tag bordered={false} style={{ fontSize: 10 }}>
              v{r.family.version}
            </Tag>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.family.description}
          </Text>
        </div>
      ),
    },
    {
      title: 'Bound provider',
      key: 'provider',
      width: 240,
      render: (_, r) =>
        r.binding ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="ol-mono" style={{ fontSize: 12.5, fontWeight: 600 }}>
                {r.binding.providerId}
              </span>
              <TierBadge tier={tiers[r.binding.providerId]} />
            </div>
            {r.binding.enabled ? (
              <span style={{ color: 'var(--ol-positive)', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, marginTop: 2 }}>
                <CheckCircleFilled aria-hidden /> Enabled
              </span>
            ) : (
              <span style={{ color: 'var(--ol-attention)', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, marginTop: 2 }}>
                <MinusCircleOutlined aria-hidden /> Disabled
              </span>
            )}
          </div>
        ) : (
          <span style={{ color: 'var(--ol-text-tertiary)', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
            <MinusCircleOutlined aria-hidden /> Not bound
          </span>
        ),
    },
    {
      title: '',
      key: 'action',
      width: 130,
      align: 'right',
      render: (_, r) => (
        <Button
          size="small"
          icon={<LinkOutlined />}
          disabled={bindingsUnavailable}
          onClick={() => setEditFamily(r.family)}
        >
          {r.binding ? 'Rebind' : 'Bind'}
        </Button>
      ),
    },
  ];

  return (
    <div className="ol-page">
      <PageHeader
        title="Integrations"
        icon={<ApiOutlined />}
        subtitle={
          <>
            Both halves of this hub&apos;s integration surface. Inbound: the capability fabric — the
            canonical family catalogue, which provider each family is bound to, and the
            identity-rail entitlements that gate binding. Outbound: signed webhook subscriptions
            over the closed event catalogue, with their delivery ledger.
          </>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
            Refresh
          </Button>
        }
      />

      {bindingsUnavailable && !familiesDown && (
        <div
          className="ol-card"
          style={{ margin: '12px 0', borderColor: 'color-mix(in srgb, var(--ol-attention) 40%, var(--ol-border))' }}
        >
          <Text style={{ fontSize: 12.5 }}>
            The capability fabric is not enabled for this hub (or its binding API is not live), so
            current bindings can't be shown and binding is disabled. The family catalogue below is
            static and always available.
          </Text>
        </div>
      )}

      <div style={{ marginTop: 16, marginBottom: 8 }}>
        <div className="ol-section">Capability registry</div>
      </div>

      {familiesDown ? (
        <EmptyState
          icon={<ApiOutlined />}
          title="Capability registry"
          description="The canonical capability families and their per-hub bindings appear here. The family catalogue is a static set of 12; bindings are hub-scoped."
          bullets={[
            'The 12 canonical capability families',
            'Which provider each family is bound to (and whether it is enabled)',
            'Connector tier (Community / Verified / Certified) on the bound provider',
          ]}
        />
      ) : (
        <Table<FamilyRow>
          rowKey={(r) => `${r.family.family}.${r.family.version}`}
          loading={loading && rows.length > 0}
          columns={columns}
          dataSource={rows}
          size="small"
          pagination={false}
          locale={{
            emptyText: loading ? (
              <TableSkeleton label="Loading capability families" rows={5} />
            ) : (
              <EmptyState
                compact
                icon={<ApiOutlined />}
                title="No capability families returned"
                description="The catalogue is a fixed set of 12 canonical families; an empty list means the fabric answered but declared none for this hub — worth raising, not a normal state."
              />
            ),
          }}
        />
      )}

      <div style={{ marginTop: 28, maxWidth: 900 }}>
        <div className="ol-section" style={{ marginBottom: 10 }}>
          Entitlements
        </div>
        <EntitlementMatrix />
      </div>

      <div style={{ marginTop: 28 }}>
        <div className="ol-section" style={{ marginBottom: 10 }}>
          Outbound — event subscriptions (webhooks)
        </div>
        <WebhooksSection />
      </div>

      <BindingDrawer
        family={editFamily}
        current={rows.find((r) => r.family === editFamily)?.binding}
        onClose={() => setEditFamily(null)}
        onBound={() => void load()}
      />
    </div>
  );
}
